import { Controller } from '@hotwired/stimulus'

// Поле «Клуб» в окне добавления тренировки (account/coach_trainings/_form).
// Подсказки — по всему каталогу, в любой стране: тренер может работать в
// разных городах. Выбор подсказки пишет id клуба в скрытое поле.
//
// Свой клуб — только если по набранному в каталоге ничего нет: тогда вместо
// подсказок один пункт «Добавить «…» — клуба нет в каталоге», и под полем
// открывается «Город клуба» (его подсказки — match_city_controller.js). От
// города — пояс и валюта тренировки; город текстом (его нет в каталоге) —
// пояс устройства из скрытого поля zone. Сервер то же правило проверяет
// сам (Account::CoachTrainingsController#create).
//
// Чипы под полем — клубы последних тренировок: каталога (с id) или свои
// (без id, с городом).
export default class TrainingClubController extends Controller {
  static targets = [
    'input',
    'dropdown',
    'id',
    'zone',
    'cityBlock',
    'cityInput',
    'cityId'
  ]
  static values = { url: String, addLabel: String }

  connect() {
    this.zoneTarget.value ||=
      new Intl.DateTimeFormat().resolvedOptions().timeZone
    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    document.addEventListener('click', this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener('click', this.onDocumentClick)
    clearTimeout(this.timer)
  }

  pickChip(event) {
    const { name, id, cityId, cityName } = event.params
    if (id) {
      this.pickClub(name, id)
    } else {
      this.cancelSearch()
      this.inputTarget.value = name
      this.idTarget.value = ''
      this.showCity(cityId, cityName)
      this.close()
    }
  }

  pickClub(name, id) {
    this.cancelSearch()
    this.inputTarget.value = name
    this.idTarget.value = id
    this.hideCity()
    this.close()
  }

  // Ввод руками сбрасывает выбранный клуб: иначе сервер получил бы старый
  // клуб под новым названием. Поле города не прячем — тренер мог просто
  // поправить опечатку в названии своего клуба.
  search() {
    clearTimeout(this.timer)
    this.idTarget.value = ''
    this.lastRequestId = (this.lastRequestId || 0) + 1
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.close()
      return
    }
    this.timer = setTimeout(() => this.fetchSuggestions(query), 300)
  }

  async fetchSuggestions(query) {
    // Отбрасываем устаревшие ответы: медленный запрос не должен
    // перерисовать подсказки поверх результатов более позднего ввода.
    const requestId = this.lastRequestId
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
      if (requestId !== this.lastRequestId || !response.ok) return

      const data = await response.json()
      if (requestId !== this.lastRequestId) return
      this.render(data.suggestions || [], query)
    } catch {
      if (requestId === this.lastRequestId) this.close()
    }
  }

  render(suggestions, query) {
    this.dropdownTarget.innerHTML = ''
    if (suggestions.length) {
      suggestions.forEach(({ id, name, label }) => {
        this.addItem(label, () => this.pickClub(name, id))
      })
    } else {
      // Ничего не нашлось — только тогда можно добавить свой клуб.
      this.addItem(this.addLabelValue.replace('__NAME__', query), () => {
        this.showCity()
        this.close()
      })
    }
    this.dropdownTarget.classList.remove('hidden')
  }

  addItem(text, onClick) {
    const item = document.createElement('button')
    item.type = 'button'
    item.className = 'tournament-club-suggestion'
    item.textContent = text
    item.addEventListener('click', onClick)
    this.dropdownTarget.appendChild(item)
  }

  showCity(cityId, cityName) {
    this.cityBlockTarget.classList.remove('hidden')
    if (cityName) {
      this.cityInputTarget.value = cityName
      this.cityIdTarget.value = cityId || ''
    }
    if (!this.cityInputTarget.value) this.cityInputTarget.focus()
  }

  hideCity() {
    this.cityBlockTarget.classList.add('hidden')
    this.cityInputTarget.value = ''
    this.cityIdTarget.value = ''
  }

  // Выбор сделан — ответ подсказок, который ещё в пути, не должен снова
  // раскрыть список под уже выбранным клубом.
  cancelSearch() {
    clearTimeout(this.timer)
    this.lastRequestId = (this.lastRequestId || 0) + 1
  }

  close() {
    this.dropdownTarget.classList.add('hidden')
    this.dropdownTarget.innerHTML = ''
  }
}
